import React from 'react';
import { Card, Typography, Row, Col, Statistic, List, Space, Tag, Table, Spin, Tooltip as AntTooltip } from 'antd';
import { FallOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useGetInformesGastos } from '../../../hooks/useInformes';

const { Title, Text } = Typography;

const SeccionGastos = ({ desde, hasta }) => {

    const { data, isLoading, isError } = useGetInformesGastos({ desde, hasta });

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px', color: '#64748b' }}>Procesando egresos...</div>
            </div>
        );
    }

    if (isError || !data) {
        return <div style={{ textAlign: 'center', marginTop: '50px' }}><Text type="danger">Error al cargar el detalle de gastos.</Text></div>;
    }

    const { kpis, categorias, ultimosGastos } = data;

    return (
        <div style={{ animation: 'fadeIn 0.5s' }}>
            {/* KPIs Específicos de Gastos */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#fef2f2', borderRadius: '12px', border: '1px solid #fee2e2' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '14px' }}>Total Gastos / Egresos</Text>
                                    <AntTooltip title="Suma de todo el dinero que salió del negocio por concepto de gastos operativos en este rango de fechas." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#f87171', cursor: 'help' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.total}
                            prefix="$"
                            formatter={val => val.toLocaleString('es-CO')}
                            valueStyle={{ fontWeight: '700', color: '#ef4444' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#f8fafc', borderRadius: '12px' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '14px' }}>Promedio Gasto Diario</Text>
                                    <AntTooltip title="Indica a qué ritmo está gastando dinero el negocio día a día." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#94a3b8', cursor: 'help' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.promedioDiario}
                            prefix="$"
                            formatter={val => val.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
                            valueStyle={{ fontWeight: '700', color: '#475569' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#f8fafc', borderRadius: '12px' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '14px' }}>Mayor Fuga de Capital</Text>
                                    <AntTooltip title="Es la categoría en la que más se ha gastado dinero durante este periodo." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#94a3b8', cursor: 'help' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.categoriaMayor}
                            prefix={<FallOutlined style={{ color: '#ef4444' }} />}
                            valueStyle={{ fontWeight: '700', color: '#0f172a', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* Gráfico Visual de Gastos por Categoría */}
                <Col xs={24} md={10}>
                    <Card
                        title={
                            <Space>
                                <Title level={5} style={{ margin: 0 }}>Gastos por Categoría</Title>
                                <AntTooltip title="Muestra en qué rubros específicos se está yendo el dinero, ordenado de mayor a menor impacto." color="#0f172a">
                                    <InfoCircleOutlined style={{ color: '#94a3b8', cursor: 'help', fontSize: '14px' }} />
                                </AntTooltip>
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', height: '100%' }}
                    >
                        {categorias.length === 0 ? <Text type="secondary">No hay gastos registrados en este periodo.</Text> : (
                            <List
                                dataSource={categorias}
                                renderItem={item => {
                                    const porcentaje = kpis.total > 0 ? ((Number(item.valor) / kpis.total) * 100).toFixed(1) : 0;

                                    return (
                                        <List.Item style={{ border: 'none', padding: '12px 0', display: 'block' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <Space>
                                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: item.color }}></div>
                                                    <Text strong style={{ color: '#475569' }}>{item.categoria}</Text>
                                                </Space>
                                                <Text strong style={{ color: '#ef4444' }}>
                                                    ${Number(item.valor).toLocaleString('es-CO', { maximumFractionDigits: 0 })}
                                                </Text>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                                    <div style={{ width: `${porcentaje}%`, background: item.color, height: '100%', borderRadius: '4px' }}></div>
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px', width: '40px', textAlign: 'right' }}>{porcentaje}%</Text>
                                            </div>
                                        </List.Item>
                                    );
                                }}
                            />
                        )}
                    </Card>
                </Col>

                {/* Tabla de los últimos egresos importantes */}
                <Col xs={24} md={14}>
                    <Card
                        title={
                            <Space>
                                <Title level={5} style={{ margin: 0 }}>Últimos Egresos Registrados</Title>
                                <AntTooltip title="Historial detallado de los gastos más recientes dentro de la fecha seleccionada." color="#0f172a">
                                    <InfoCircleOutlined style={{ color: '#94a3b8', cursor: 'help', fontSize: '14px' }} />
                                </AntTooltip>
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', height: '100%' }}
                    >
                        <Table
                            dataSource={ultimosGastos}
                            rowKey="id"
                            pagination={{ pageSize: 6 }}
                            columns={[
                                { title: 'Fecha', dataIndex: 'fecha', width: 110, render: (text) => <Text type="secondary">{text}</Text> },
                                { title: 'Descripción', dataIndex: 'descripcion', render: (text) => <Text strong>{text}</Text> },
                                { title: 'Categoría', dataIndex: 'categoria', render: (text, record) => <Tag color={record.color} style={{ border: 'none' }}>{text}</Tag> },
                                { title: 'Monto', dataIndex: 'monto', align: 'right', render: (val) => <Text strong style={{ color: '#ef4444' }}>- ${val.toLocaleString('es-CO')}</Text> },
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default SeccionGastos;